import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, Grid,
  ToggleButtonGroup, ToggleButton, FormControl, InputLabel, Select, MenuItem,
  Chip, OutlinedInput, Autocomplete, IconButton, Typography, Alert,
  FormControlLabel, Checkbox, CircularProgress,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useAuth } from '../../../contexts/AuthContext';
import { cardManagementService } from '../../../services/cardManagementService';
import pdksService, { brandLabel, pdksCardNumber, type PdksDevice } from '../../../services/pdksService';
import CardPreview from '../components/CardPreview';
import GuideButton from '../../common/guide/GuideButton';
import { useTranslation } from 'react-i18next';

function generateCardNumber(): string {
  const digits = Array.from({ length: 16 }, () => Math.floor(Math.random() * 10)).join('');
  return `${digits.slice(0, 4)} ${digits.slice(4, 8)} ${digits.slice(8, 12)} ${digits.slice(12, 16)}`;
}

type OwnerType = 'ACCOUNT' | 'PERSONNEL';

interface AddCardModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  cardTypes: any[];
  wallets: any[];
  accounts: any[];
  /** Personel listesi: { id (=firebaseUid), name } */
  personnel?: any[];
  editCard?: any;
}

const AddCardModal: React.FC<AddCardModalProps> = ({ open, onClose, onSuccess, cardTypes, wallets, accounts, personnel = [], editCard }) => {
  const { t, i18n } = useTranslation(['cardManagement', 'common2']);
  const isEnglish = i18n.language?.toLowerCase().startsWith('en');
  const tr = (trText: string, enText: string) => (isEnglish ? enText : trText);
  const cm = (key: string, defaultValue: string) => t(key, { ns: 'cardManagement', defaultValue });
  const { currentUser, userDetails, companyName: authCompanyName } = useAuth();
  const companyId = userDetails?.companyId || '';
  const companyName = authCompanyName || '';

  const [mode, setMode] = useState<'PHYSICAL' | 'VIRTUAL'>('PHYSICAL');
  const [cardUID, setCardUID] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [cardTypeId, setCardTypeId] = useState<number | ''>('');
  const [selectedWalletIds, setSelectedWalletIds] = useState<number[]>([]);
  const [ownerType, setOwnerType] = useState<OwnerType>('ACCOUNT');
  const [selectedAccount, setSelectedAccount] = useState<any>(null);
  const [selectedPersonnel, setSelectedPersonnel] = useState<any>(null);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deletedCardId, setDeletedCardId] = useState<number | null>(null);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [pdksDevices, setPdksDevices] = useState<PdksDevice[]>([]);
  const [pdksSelected, setPdksSelected] = useState<number[]>([]);
  const [pdksReady, setPdksReady] = useState(false);
  const [pdksLoading, setPdksLoading] = useState(false);
  const [initialPersonnelUid, setInitialPersonnelUid] = useState<string | null>(null);

  const isEditMode = !!editCard;

  useEffect(() => {
    if (open && editCard) {
      setMode(editCard.cardSource === 'VIRTUAL' ? 'VIRTUAL' : 'PHYSICAL');
      setCardUID(editCard.cardUID || '');
      setCardNumber(editCard.cardNumber || '');
      setExpiryDate(editCard.expiryDate || '');
      setCvv(editCard.cvv || '');
      setCardTypeId(editCard.cardTypeId || '');
      const wIds = editCard.walletIds || (editCard.wallets || []).map((w: any) => w.walletId).filter(Boolean);
      setSelectedWalletIds(wIds);
      setNotes(editCard.notes || '');

      const isPersonnelOwned = editCard.ownerType === 'PERSONNEL';
      setOwnerType(isPersonnelOwned ? 'PERSONNEL' : 'ACCOUNT');

      if (isPersonnelOwned) {
        // Ad, listeden canlı çözülür; snapshot bayatlamışsa güncel ad görünür.
        const matchedPersonnel = personnel.find((p: any) => String(p.id) === String(editCard.personnelFirebaseUid));
        if (matchedPersonnel) {
          setSelectedPersonnel(matchedPersonnel);
        } else if (editCard.personnelFirebaseUid) {
          setSelectedPersonnel({ id: editCard.personnelFirebaseUid, name: editCard.personnelName || '' });
        }
        setInitialPersonnelUid(editCard.personnelFirebaseUid || null);
      } else {
        const matchedAccount = accounts.find((a: any) => String(a.id) === String(editCard.accountId));
        if (matchedAccount) {
          setSelectedAccount(matchedAccount);
        } else if (editCard.accountName) {
          setSelectedAccount({ id: editCard.accountId, companyName: editCard.accountName });
        }
      }
      return;
    }
    if (open && !editCard) {
      if (mode === 'VIRTUAL') setCardNumber(generateCardNumber());
      const generalCari = accounts.find((a: any) => a.companyName === 'Genel Cari' || a.accountCode === 'GENEL');
      if (generalCari) setSelectedAccount(generalCari);
      setInitialPersonnelUid(null);
    }
  }, [open, editCard, accounts, personnel, mode]);

  const isPersonnelOwner = ownerType === 'PERSONNEL';
  const showPdks = isPersonnelOwner && mode === 'PHYSICAL';

  useEffect(() => {
    if (!open || !showPdks) return;
    let cancelled = false;
    const load = async () => {
      setPdksLoading(true);
      try {
        const res = await pdksService.getDevices();
        if (cancelled) return;
        if (res.data.tablesMissing) {
          setPdksDevices([]);
          setPdksReady(false);
          return;
        }
        const list: PdksDevice[] = (res.data.data || []).flatMap((a: any) => a.devices || []);
        setPdksDevices(list);
        setPdksReady(true);
        const uid = selectedPersonnel?.id;
        if (uid) {
          const asg = await pdksService.getPersonnelAssignments(String(uid));
          if (!cancelled && asg.data?.success) {
            setPdksSelected((asg.data.data || []).map((x: any) => x.deviceId));
          }
        } else {
          setPdksSelected([]);
        }
      } catch {
        if (!cancelled) {
          setPdksDevices([]);
          setPdksReady(false);
        }
      } finally {
        if (!cancelled) setPdksLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open, showPdks, selectedPersonnel?.id]);

  const handleModeChange = (_: any, newMode: 'PHYSICAL' | 'VIRTUAL') => {
    if (newMode) {
      setMode(newMode);
      setCardUID('');
      setCardNumber(newMode === 'VIRTUAL' ? generateCardNumber() : '');
    }
  };

  const formatCardNumberInput = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 16);
    const parts = [];
    for (let i = 0; i < digits.length; i += 4) {
      parts.push(digits.slice(i, i + 4));
    }
    return parts.join(' ');
  };

  const formatExpiryInput = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 4);
    if (digits.length > 2) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return digits;
  };

  const selectedCardType = useMemo(() => cardTypes.find((ct: any) => ct.id === cardTypeId), [cardTypes, cardTypeId]);

  const ownerName = isPersonnelOwner
    ? (selectedPersonnel?.name || '')
    : (selectedAccount?.companyName || '');

  // Sahiplik alanları tek yerden üretilir: backend OwnerType'ı görünce karşı tarafın
  // alanlarını temizler, bu yüzden yalnız seçili tarafı göndermek yeterli.
  const ownerPayload = useMemo(() => {
    if (isPersonnelOwner) {
      return {
        OwnerType: 'PERSONNEL',
        PersonnelFirebaseUid: selectedPersonnel?.id?.toString() || '',
        PersonnelName: selectedPersonnel?.name || '',
      };
    }
    // Düzenlemede cari seçimi çözülemediyse sahipliğe hiç dokunmayıp eski davranışı
    // koruruz; aksi halde mevcut cari bağı sessizce silinirdi.
    if (isEditMode && !selectedAccount) return {};
    return {
      OwnerType: 'ACCOUNT',
      AccountId: selectedAccount?.id?.toString() || '',
      AccountName: selectedAccount?.companyName || (isEditMode ? '' : 'Genel Cari'),
    };
  }, [isPersonnelOwner, selectedPersonnel, selectedAccount, isEditMode]);

  const handleRestore = async () => {
    if (!deletedCardId) return;
    setRestoreLoading(true);
    try {
      await cardManagementService.restoreCard({ Id: deletedCardId, CompanyId: companyId });
      handleClose();
      onSuccess();
    } catch {
      setError(cm('addModal.messages.restoreError', 'An error occurred while restoring card'));
    }
    setRestoreLoading(false);
  };

  const handleSubmit = async () => {
    const trimmedCardNumber = cardNumber.replace(/\s/g, '');
    if (!trimmedCardNumber) {
      setError(cm('addModal.messages.cardNumberRequired', 'Card number cannot be empty'));
      return;
    }
    if (mode === 'VIRTUAL' && trimmedCardNumber.length !== 16) {
      setError(cm('addModal.messages.virtualCardLength', 'Virtual card number must be 16 digits'));
      return;
    }
    if (!cardTypeId) { setError(cm('addModal.messages.cardTypeRequired', 'Select a card type')); return; }
    if (selectedWalletIds.length === 0) { setError(cm('addModal.messages.walletRequired', 'Select at least one wallet')); return; }
    if (isPersonnelOwner && !selectedPersonnel?.id) {
      setError(cm('addModal.messages.personnelRequired', 'Select a personnel'));
      return;
    }
    // Düzenlemede cari çözülemediyse sahiplik yazılmaz (ownerPayload boş kalır);
    // kullanıcı bunu sessiz bir başarı sanmasın diye açıkça uyarılır.
    if (!isPersonnelOwner && isEditMode && !selectedAccount) {
      setError(cm('addModal.messages.accountRequired', 'Select an account'));
      return;
    }

    setLoading(true);
    setError('');
    setDeletedCardId(null);
    try {
      if (isEditMode) {
        await cardManagementService.updateCard({
          Id: editCard.id,
          CompanyId: companyId,
          CardTypeId: cardTypeId || undefined,
          CardUID: mode === 'PHYSICAL' ? cardUID || undefined : undefined,
          CVV: cvv || undefined,
          ...ownerPayload,
          Notes: notes || undefined,
          ExpiryDate: expiryDate || undefined,
          WalletIds: selectedWalletIds.length > 0 ? selectedWalletIds : undefined,
        });
      } else {
        const checkRes = await cardManagementService.checkCardExists(companyId, {
          cardNumber: trimmedCardNumber,
          cardUID: mode === 'PHYSICAL' && cardUID ? cardUID : undefined,
        });
        const checkData = checkRes.data;
        if (checkData?.existsActive) {
          setError(cm('addModal.messages.cardAlreadyExists', 'This card already exists. Existing cards cannot be added again.'));
          setLoading(false);
          return;
        }
        if (checkData?.existsDeleted && checkData?.deletedCardId) {
          setDeletedCardId(checkData.deletedCardId);
          setError(cm('addModal.messages.cardDeletedRestore', 'This card is deleted. Please restore the deleted card.'));
          setLoading(false);
          return;
        }

        await cardManagementService.createCard({
          CompanyId: companyId,
          CardTypeId: cardTypeId,
          CardSource: mode,
          CardUID: mode === 'PHYSICAL' ? cardUID || null : null,
          CardNumber: cardNumber,
          CVV: cvv || null,
          ...ownerPayload,
          Notes: notes || null,
          ExpiryDate: expiryDate || null,
          WalletIds: selectedWalletIds,
          CreatedBy: currentUser?.email || '',
        });
      }

      const cardNo = pdksCardNumber({ cardUID, cardNumber });
      if (isPersonnelOwner && mode === 'PHYSICAL' && selectedPersonnel?.id && (isEditMode || pdksSelected.length > 0)) {
        await pdksService.syncFromCard({
          personnelFirebaseUid: String(selectedPersonnel.id),
          displayName: selectedPersonnel.name || '',
          cardNumber: cardNo || null,
          deviceIds: pdksSelected,
        });
      } else if (isEditMode && initialPersonnelUid && !isPersonnelOwner) {
        await pdksService.syncFromCard({
          personnelFirebaseUid: String(initialPersonnelUid),
          deviceIds: [],
        });
      }

      handleClose();
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || (isEditMode ? cm('addModal.messages.updateError', 'An error occurred while updating card') : cm('addModal.messages.createError', 'An error occurred while creating card')));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setMode('PHYSICAL');
    setCardUID('');
    setCardNumber('');
    setExpiryDate('');
    setCvv('');
    setCardTypeId('');
    setSelectedWalletIds([]);
    setOwnerType('ACCOUNT');
    setSelectedAccount(null);
    setSelectedPersonnel(null);
    setNotes('');
    setError('');
    setDeletedCardId(null);
    setRestoreLoading(false);
    setPdksDevices([]);
    setPdksSelected([]);
    setPdksReady(false);
    setInitialPersonnelUid(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {isEditMode ? cm('modals.editCardTitle', 'Edit Card') : cm('modals.addCardTitle', 'Add New Card')}
        <GuideButton guideKey="cards.addCard" />
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 0.5 }}>
          {/* Form */}
          <Grid item xs={12} md={7}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <ToggleButtonGroup value={mode} exclusive onChange={handleModeChange} fullWidth size="small" disabled={isEditMode}>
                <ToggleButton value="PHYSICAL">{cm('addModal.fields.physicalCard', 'Physical Card')}</ToggleButton>
                <ToggleButton value="VIRTUAL">{cm('addModal.fields.virtualCard', 'Virtual Card')}</ToggleButton>
              </ToggleButtonGroup>

              {mode === 'PHYSICAL' && (
                <TextField
                  label={cm('addModal.fields.cardUid', 'Card UID')}
                  value={cardUID}
                  onChange={(e) => {
                    const uid = e.target.value;
                    setCardUID(uid);
                    setCardNumber(uid);
                  }}
                  size="small"
                  placeholder={cm('addModal.fields.cardUidPlaceholder', 'UID read from card reader')}
                  disabled={isEditMode}
                />
              )}

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  label={cm('addModal.fields.cardNumber', 'Card Number')}
                  value={mode === 'VIRTUAL' ? cardNumber : cardNumber}
                  onChange={(e) => setCardNumber(mode === 'VIRTUAL' ? formatCardNumberInput(e.target.value) : e.target.value)}
                  size="small"
                  fullWidth
                  placeholder={mode === 'VIRTUAL' ? 'XXXX XXXX XXXX XXXX' : cm('addModal.fields.cardNumberPlaceholderPhysical', 'UID is auto-filled, editable')}
                  disabled={mode === 'VIRTUAL' || isEditMode}
                  inputProps={{ maxLength: mode === 'VIRTUAL' ? 19 : 50 }}
                  helperText={mode === 'PHYSICAL' ? cm('addModal.fields.cardNumberHelperPhysical', 'Auto-fills when UID is entered, you can edit it') : ''}
                />
                {mode === 'VIRTUAL' && (
                  <IconButton onClick={() => setCardNumber(generateCardNumber())} size="small" sx={{ mt: 0.5 }}>
                    <RefreshIcon />
                  </IconButton>
                )}
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label={cm('addModal.fields.expiry', 'Expiry (MM/YY)')}
                  value={expiryDate}
                  onChange={(e) => setExpiryDate(formatExpiryInput(e.target.value))}
                  size="small"
                  sx={{ flex: 1 }}
                  inputProps={{ maxLength: 5 }}
                />
                <TextField
                  label="CVV"
                  value={cvv}
                  onChange={(e) => setCvv(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  size="small"
                  sx={{ flex: 1 }}
                  inputProps={{ maxLength: 4 }}
                />
              </Box>

              <FormControl size="small" required>
                <InputLabel>{cm('addModal.fields.cardType', 'Card Type')}</InputLabel>
                <Select value={cardTypeId} onChange={(e) => setCardTypeId(e.target.value as number)} label={cm('addModal.fields.cardType', 'Card Type')}>
                  {cardTypes.map((ct: any) => (
                    <MenuItem key={ct.id} value={ct.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {ct.color && <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: ct.color }} />}
                        {ct.name}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" required>
                <InputLabel>{cm('addModal.fields.wallets', 'Wallets')}</InputLabel>
                <Select
                  multiple
                  value={selectedWalletIds}
                  onChange={(e) => setSelectedWalletIds(e.target.value as number[])}
                  input={<OutlinedInput label={cm('addModal.fields.wallets', 'Wallets')} />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as number[]).map((id) => {
                        const w = wallets.find((w: any) => w.id === id);
                        return <Chip key={id} label={w?.name || id} size="small" />;
                      })}
                    </Box>
                  )}
                >
                  {wallets.map((w: any) => (
                    <MenuItem key={w.id} value={w.id}>{w.name} ({w.currency})</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  {cm('addModal.fields.cardOwner', 'Card Owner')}
                </Typography>
                <ToggleButtonGroup
                  value={ownerType}
                  exclusive
                  onChange={(_, val: OwnerType | null) => { if (val) setOwnerType(val); }}
                  fullWidth
                  size="small"
                  sx={{ mb: 1.5 }}
                >
                  <ToggleButton value="ACCOUNT">{cm('addModal.fields.ownerAccount', 'Account')}</ToggleButton>
                  <ToggleButton value="PERSONNEL">{cm('addModal.fields.ownerPersonnel', 'Personnel')}</ToggleButton>
                </ToggleButtonGroup>

                {isPersonnelOwner ? (
                  <Autocomplete
                    options={personnel}
                    getOptionLabel={(opt: any) => opt.name || ''}
                    value={selectedPersonnel}
                    onChange={(_, val) => setSelectedPersonnel(val)}
                    renderInput={(params) => <TextField {...params} label={cm('addModal.fields.personnel', 'Personnel')} size="small" required />}
                    isOptionEqualToValue={(opt, val) => String(opt?.id) === String(val?.id)}
                    size="small"
                    noOptionsText={cm('addModal.fields.noPersonnel', 'No personnel found')}
                  />
                ) : (
                  <Autocomplete
                    options={accounts}
                    getOptionLabel={(opt: any) => opt.companyName || ''}
                    value={selectedAccount}
                    onChange={(_, val) => setSelectedAccount(val)}
                    renderInput={(params) => <TextField {...params} label={cm('addModal.fields.account', 'Account')} size="small" required />}
                    isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                    size="small"
                  />
                )}
              </Box>

              <TextField label={cm('addModal.fields.notes', 'Notes')} value={notes} onChange={(e) => setNotes(e.target.value)} size="small" multiline rows={2} />

              {showPdks && (
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    {tr('PDKS terminalleri', 'PDKS terminals')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    {tr(
                      'Bu kart devam kontrolünde kullanılacaksa cihazları burada seçin. Kart numarası tekrar yazılmaz. Baştaki sıfırlar yazılmasa da terminal 10 haneye tamamlar (16627763 → 0016627763).',
                      'If this card is used for attendance, pick the terminals here. The card number is not entered again. Leading zeros may be omitted; the terminal is padded to 10 digits (16627763 → 0016627763).'
                    )}
                  </Typography>
                  {pdksLoading && <CircularProgress size={20} />}
                  {!pdksLoading && !pdksReady && (
                    <Alert severity="info">
                      {tr('PDKS henüz bu şirkette açık değil veya cihaz yok.', 'PDKS is not set up for this company, or there are no devices yet.')}
                    </Alert>
                  )}
                  {!pdksLoading && pdksReady && pdksDevices.length === 0 && (
                    <Alert severity="info">
                      {tr('Henüz PDKS cihazı yok. Cihaz Yönetimi’nden PDKS ekranı oluşturun.', 'No PDKS devices yet. Create a PDKS screen in Device Management.')}
                    </Alert>
                  )}
                  {!pdksLoading && pdksDevices.length > 1 && (
                    <Button size="small" onClick={() => setPdksSelected(pdksDevices.map((d) => d.id))} sx={{ mb: 0.5 }}>
                      {tr('Tümünü seç', 'Select all')}
                    </Button>
                  )}
                  {pdksDevices.map((d) => (
                    <FormControlLabel
                      key={d.id}
                      sx={{ display: 'block' }}
                      control={(
                        <Checkbox
                          size="small"
                          checked={pdksSelected.includes(d.id)}
                          onChange={(e) => setPdksSelected((prev) => (
                            e.target.checked ? [...prev, d.id] : prev.filter((id) => id !== d.id)
                          ))}
                        />
                      )}
                      label={`${d.name || d.model || d.deviceKey} — ${brandLabel(d.brand)}`}
                    />
                  ))}
                </Box>
              )}

              {error && (
                <Alert
                  severity={deletedCardId ? 'warning' : 'error'}
                  action={deletedCardId ? (
                    <Button color="inherit" size="small" onClick={handleRestore} disabled={restoreLoading}>
                      {restoreLoading ? cm('common.loading', 'Loading...') : cm('common.restore', 'Restore')}
                    </Button>
                  ) : undefined}
                >
                  {error}
                </Alert>
              )}
            </Box>
          </Grid>

          {/* Preview */}
          <Grid item xs={12} md={5}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, pt: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">{cm('addModal.cardPreview', 'Card Preview')}</Typography>
              <CardPreview
                cardNumber={cardNumber}
                holderName={ownerName || cm('addModal.fields.generalAccount', 'General Account')}
                expiryDate={expiryDate}
                cvv={cvv}
                companyName={companyName}
                cardTypeName={selectedCardType?.name}
                cardTypeColor={selectedCardType?.color}
              />
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                {cm('addModal.flipHint', 'Click on the card to flip')}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{cm('common.cancel', 'Cancel')}</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? (isEditMode ? cm('addModal.updating', 'Updating...') : cm('addModal.creating', 'Creating...')) : (isEditMode ? cm('addModal.update', 'Update') : cm('addModal.createCard', 'Create Card'))}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddCardModal;
